import { Token, Currency, CurrencyAmount, TokenAmount, TradeType } from '@uniswap/sdk-core'
import { encodeRouteToPath, Route, Trade } from '@uniswap/v3-sdk'
import { BigNumber } from 'ethers'
import { useMemo } from 'react'
import { useSingleContractMultipleData } from '../state/multicall/hooks'
import { useAllV3Routes } from './useAllV3Routes'
import { useV3Quoter } from './useContract'
import { formatUnits } from 'ethers/lib/utils'
export enum V3TradeState {
  LOADING,
  INVALID,
  NO_ROUTE_FOUND,
  VALID,
  SYNCING,
}

/* function getEffectiveLiquidity(liquidity: any): bigint {
  if (!liquidity) return BigInt(0)

  if (Array.isArray(liquidity)) {
    if (liquidity.length === 0) return BigInt(0)

    const low = BigInt(liquidity[0])
    const high = liquidity[1] !== undefined ? BigInt(liquidity[1]) << BigInt(32) : BigInt(0)

    return low + high
  }

  try {
    return BigInt(liquidity.toString())
  } catch {
    return BigInt(0)
  }
}

function getTopRoutesByLiquidity(routes: any[], topN = 7): any[] {
  return routes
    .map((route) => {
      const minLiquidity = route.pools.reduce((min: any, pool: any) => {
        const liq = getEffectiveLiquidity(pool.liquidity)
        return liq < min ? liq : min
      }, BigInt('0xffffffffffffffffffffffffffffffff')) // large default
      console.log(route, minLiquidity)
      return { route, minLiquidity }
    })
    .sort((a, b) => (b.minLiquidity > a.minLiquidity ? 1 : -1)) // descending
    .slice(0, topN)
    .map(({ route }) => route) // return only the routes
} */
/**
 * Returns the best v3 trade for a desired exact input swap
 * @param amountIn the amount to swap in
 * @param currencyOut the desired output currency
 */
export function useBestV3TradeExactIn(
  amountIn?: CurrencyAmount,
  currencyOut?: Currency
): { state: V3TradeState; trade: Trade | null } {
  const quoter = useV3Quoter()
  const { routes, loading: routesLoading } = useAllV3Routes(amountIn?.currency, currencyOut)

  const usedRoutes = /*  getTopRoutesByLiquidity(routes, 7) */ routes

  const impactedPools: any = []

  usedRoutes.map((a: any) => {
    a?.pools.map((x: any) => {
      const poolInfo = `Pool ${x.token0?.symbol}/${x.token1?.symbol}-${x.fee / 10000}%`
      if (!impactedPools.includes(poolInfo)) {
        impactedPools.push(poolInfo)
      }
    })
  })

  /*   impactedPools.length > 0 && console.log('impacted pools', impactedPools) */

  const quoteExactInInputs = useMemo(() => {
    return usedRoutes.map((route) => [
      encodeRouteToPath(route, false),
      amountIn ? `0x${amountIn.raw.toString(16)}` : undefined,
    ])
  }, [amountIn, usedRoutes])

  const quotesResults = useSingleContractMultipleData(quoter, 'quoteExactInput', quoteExactInInputs)

  const logsQuotes: string[] = []
  let x = ''
  usedRoutes.map((a, index) => {
    x = `Expected output: ${formatUnits(
      quotesResults[index]?.result?.amountOut || BigNumber.from(0),
      currencyOut?.decimals || 18
    )} with routes: `
    a?.pools?.map((b, index) => {
      x = `${x} ${b?.token0?.symbol}/${b?.token1?.symbol} (${b?.fee / 10000}%)${
        index !== a?.pools.length - 1 ? ' > ' : ''
      }`
    })
    logsQuotes.push(x)
    x = ''
  })
  logsQuotes.length > 0 && console.log(logsQuotes)

  return useMemo(() => {
    if (!amountIn || !currencyOut) {
      return {
        state: V3TradeState.INVALID,
        trade: null,
      }
    }

    if (routesLoading || quotesResults.some(({ loading }) => loading)) {
      return {
        state: V3TradeState.LOADING,
        trade: null,
      }
    }

    const { bestRoute, amountOut } = quotesResults.reduce(
      (currentBest: { bestRoute: Route | null; amountOut: BigNumber | null }, { result }, i) => {
        if (!result) return currentBest

        if (currentBest.amountOut === null) {
          return {
            bestRoute: routes[i],
            amountOut: result.amountOut,
          }
        } else if (currentBest.amountOut.lt(result.amountOut)) {
          return {
            bestRoute: routes[i],
            amountOut: result.amountOut,
          }
        }

        return currentBest
      },
      {
        bestRoute: null,
        amountOut: null,
      }
    )

    if (!bestRoute || !amountOut) {
      return {
        state: V3TradeState.NO_ROUTE_FOUND,
        trade: null,
      }
    }

    let y = `Best Route:`
    bestRoute?.pools?.forEach((z, index) => {
      y = `${y} ${z.token0?.symbol}/${z.token1?.symbol} (${z?.fee / 10000}%)${
        index !== bestRoute?.pools?.length - 1 ? ' > ' : ''
      }`
    })

    console.log(y)

    const isSyncing = quotesResults.some(({ syncing }) => syncing)

    return {
      state: isSyncing ? V3TradeState.SYNCING : V3TradeState.VALID,
      trade: Trade.createUncheckedTrade({
        route: bestRoute,
        tradeType: TradeType.EXACT_INPUT,
        inputAmount: amountIn,
        outputAmount:
          currencyOut instanceof Token
            ? new TokenAmount(currencyOut, amountOut.toString())
            : CurrencyAmount.ether(amountOut.toString()),
      }),
    }
  }, [amountIn, currencyOut, quotesResults, routes, routesLoading])
}

/**
 * Returns the best v3 trade for a desired exact output swap
 * @param currencyIn the desired input currency
 * @param amountOut the amount to swap out
 */
export function useBestV3TradeExactOut(
  currencyIn?: Currency,
  amountOut?: CurrencyAmount
): { state: V3TradeState; trade: Trade | null } {
  const quoter = useV3Quoter()
  const { routes, loading: routesLoading } = useAllV3Routes(currencyIn, amountOut?.currency)
  const usedRoutes = routes

  const quoteExactOutInputs = useMemo(() => {
    return usedRoutes.map((route) => [
      encodeRouteToPath(route, true),
      amountOut ? `0x${amountOut.raw.toString(16)}` : undefined,
    ])
  }, [amountOut, usedRoutes])

  const quotesResults = useSingleContractMultipleData(quoter, 'quoteExactOutput', quoteExactOutInputs)

  return useMemo(() => {
    if (!amountOut || !currencyIn || quotesResults.some(({ valid }) => !valid)) {
      return {
        state: V3TradeState.INVALID,
        trade: null,
      }
    }

    if (routesLoading || quotesResults.some(({ loading }) => loading)) {
      return {
        state: V3TradeState.LOADING,
        trade: null,
      }
    }

    const { bestRoute, amountIn } = quotesResults.reduce(
      (currentBest: { bestRoute: Route | null; amountIn: BigNumber | null }, { result }, i) => {
        if (!result) return currentBest

        if (currentBest.amountIn === null) {
          return {
            bestRoute: routes[i],
            amountIn: result.amountIn,
          }
        } else if (currentBest.amountIn.gt(result.amountIn)) {
          return {
            bestRoute: routes[i],
            amountIn: result.amountIn,
          }
        }

        return currentBest
      },
      {
        bestRoute: null,
        amountIn: null,
      }
    )

    if (!bestRoute || !amountIn) {
      return {
        state: V3TradeState.NO_ROUTE_FOUND,
        trade: null,
      }
    }

    const isSyncing = quotesResults.some(({ syncing }) => syncing)

    return {
      state: isSyncing ? V3TradeState.SYNCING : V3TradeState.VALID,
      trade: Trade.createUncheckedTrade({
        route: bestRoute,
        tradeType: TradeType.EXACT_OUTPUT,
        inputAmount:
          currencyIn instanceof Token
            ? new TokenAmount(currencyIn, amountIn.toString())
            : CurrencyAmount.ether(amountIn.toString()),
        outputAmount: amountOut,
      }),
    }
  }, [amountOut, currencyIn, quotesResults, routes, routesLoading])
}
